/**
 * FILTER UX V2 — native pill rail behavior for Blink/SEO collections (2026-07-16,
 * AJAX rework 2026-09-15).
 * Loaded only by snippets/mt-blink-pillrail.liquid (Blink collection template).
 *
 * Filtering used to be GET-based: every checkbox tap reloaded the whole page and a
 * sessionStorage flag reopened the sheet afterwards (visible flash + repop — Amy).
 * Now applies render through Shopify's Section Rendering API: fetch the section for
 * the filtered URL and swap ONLY the parts that change (grid, rail contents, drawer
 * filter contents, desktop bar contents, counts). Elements that carry listeners are
 * never replaced — all handling below is delegated to persistent nodes — so the sheet
 * stays open, scroll position holds, and the open facet stays open. Any fetch/swap
 * failure falls back to a full navigation (the old behavior).
 *
 * The old sessionStorage reopen flow is kept for those fallback navigations only.
 */
(function () {
  'use strict';

  var rail = document.getElementById('mt-bfr');
  var drawer = document.getElementById('CollectionDrawer');
  var backdrop = document.querySelector('.mt-bfr-backdrop');
  var sortSheet = document.querySelector('.mt-bfr-sortsheet');
  var mainEl = document.getElementById('MainCollection');
  if (!rail || !drawer || !mainEl) return;

  /* the template suffix that keeps the shopper in the variant — native GET
     navigations drop the view= param, so every URL we build re-adds it */
  var suffix = rail.getAttribute('data-suffix');
  function withView(href) {
    var u = new URL(href, window.location.origin);
    if (suffix) u.searchParams.set('view', suffix);
    return u.toString();
  }

  function track(name, params) {
    try {
      params = params || {};
      params.mt_variant = 'b';
      params.engine = 'native';
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    } catch (e) { /* never break the UI */ }
  }

  /* CSS var consumed by the rail's (mostly decorative) base sticky rule */
  function setHeaderOffset() {
    var header = document.querySelector('#shopify-section-header-top');
    var h = header ? Math.round(header.getBoundingClientRect().height) : 49;
    document.documentElement.style.setProperty('--mt-bfr-top', h + 'px');
  }
  setHeaderOffset();
  window.addEventListener('resize', setHeaderOffset);

  /* ==================== AJAX section rendering ==================== */

  var sectionEl = mainEl.closest('.shopify-section');
  var sectionId = sectionEl ? sectionEl.id.replace(/^shopify-section-/, '') : null;
  var inflight = null;

  function productCountEls(root) {
    return (root || document).querySelectorAll('[id="ProductCount"]');
  }

  function fillCounts() {
    /* the theme's infinite-scroll banner ("Showing N of M products") is written by a
       load-time closure we can't reach after a swap — recompute it from the DOM */
    var counts = document.getElementById('counts');
    if (!counts) return;
    var shown = document.querySelectorAll('#AjaxinateContainer > li').length;
    var pc = productCountEls()[0];
    var m = pc ? (pc.textContent.match(/(\d+)\s*products?/i) || []) : [];
    var total = m[1];
    if (shown && total) counts.textContent = 'Showing ' + shown + ' of ' + total + ' products';
  }

  function announceCounts() {
    /* a11y: the updated result count must reach screen readers after an AJAX apply
       (WCAG 4.1.3 Status Messages). The attribute survives text swaps but the element
       itself is replaced when the filter container swaps — re-applied per swap. */
    productCountEls().forEach(function (el) {
      if (!el.hasAttribute('aria-live')) el.setAttribute('aria-live', 'polite');
    });
  }

  function reinitAjaxinate() {
    /* the theme's Ajaxinate instance holds refs into the old grid — dead after a swap.
       Fresh instance on the new markup; its callback keeps the counts banner honest
       (and deliberately skips the theme's pushState('?page=N'), which wipes filter
       params from the URL). */
    if (!window.Ajaxinate) return;
    if (!document.querySelector('#AjaxinatePagination .loadmorebutton')) return;
    try {
      new window.Ajaxinate({ method: 'click', loadingText: 'Loading...', callback: fillCounts });
    } catch (e) { /* pagination is a nice-to-have — never break filtering over it */ }
  }

  function takeoverCheckboxes() {
    /* the snippet ships inline onchange="this.form.submit()" as the no-JS baseline —
       remove it so the delegated change handler below can apply via AJAX instead */
    document.querySelectorAll('form.filter__form input[type="checkbox"][onchange]')
      .forEach(function (i) { i.removeAttribute('onchange'); });
  }

  function injectViewInputs() {
    if (!suffix) return;
    document.querySelectorAll('form.filter__form').forEach(function (form) {
      if (!form.querySelector('input[name="view"]')) {
        var v = document.createElement('input');
        v.type = 'hidden';
        v.name = 'view';
        v.value = suffix;
        form.appendChild(v);
      }
    });
  }

  function dedupeLegacyChips() {
    /* the theme's Active Filters block renders each native selection twice: a native
       remove chip plus a standalone legacy blink chip — hide the legacy copy */
    var nativeChips = Array.prototype.slice.call(
      drawer.querySelectorAll('.active-filters a.active-facets__button'));
    drawer.querySelectorAll('.active-filters > div.blink-active-facets__button-inner').forEach(function (legacy) {
      var val = legacy.textContent.replace(/\s*X\s*$/, '').trim().toLowerCase();
      if (!val) return;
      var dup = nativeChips.some(function (n) { return n.textContent.toLowerCase().indexOf(val) !== -1; });
      if (dup) legacy.classList.add('mt-bfr-dup');
    });
  }

  /* ---------- facet search-within (2026-09-15, built+live-verified by subagent) ----------
     Long facets (>20 values — Artist runs ~41-73) get a type-to-filter input above the
     value list, in both the desktop bar dropdowns and the drawer (mobile sheet + desktop
     All Filters). applySwap wipes those containers via innerHTML, so this re-runs there
     and the typed query survives in a closure Map keyed by facet label. */
  var mtFacetSearchState = new Map();

  function mtFacetLabel(group) {
    var heading = group.querySelector('.blink-facet-heading');
    return heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /* real <input type=checkbox> here — a checked row is never hidden by the query */
  function mtFilterFacetRows(list, query) {
    var q = String(query || '').trim().toLowerCase();
    var rows = list.querySelectorAll(':scope > li');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var cb = row.querySelector('input[type="checkbox"]');
      if ((cb && cb.checked) || !q) { row.style.display = ''; continue; }
      var text = (row.textContent || '').toLowerCase();
      row.style.display = text.indexOf(q) === -1 ? 'none' : '';
    }
  }

  function mtWireFacetGroup(group) {
    var display = group.querySelector('.filter-group-display');
    var container = display && display.querySelector('.filter-group-display__container');
    var list = container && container.querySelector('ul.filter-group-display__list');
    if (!display || !container || !list) return; /* e.g. Price has no li list */
    var rows = list.querySelectorAll(':scope > li');
    if (rows.length <= 20) return;
    var label = mtFacetLabel(group);
    var input = display.querySelector(':scope > input.mt-facet-search');
    if (!input) {
      input = document.createElement('input');
      input.type = 'search';
      input.setAttribute('inputmode', 'search');
      input.placeholder = 'Search';
      input.className = 'mt-facet-search';
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('aria-label', 'Search ' + (label || 'facet') + ' options');
      display.insertBefore(input, container);
      input.addEventListener('click', function (e) { e.stopPropagation(); });
      input.addEventListener('keydown', function (e) {
        /* Escape with a query clears it; Escape on an EMPTY box bubbles so the
           existing document handler can close the sheet/dropdown. Other keys are
           stopped so typing is never misread by outside-click/toggle handlers. */
        if (e.key === 'Escape') {
          if (input.value) {
            e.preventDefault();
            e.stopPropagation();
            input.value = '';
            mtFacetSearchState.set(label, '');
            mtFilterFacetRows(list, '');
          }
          return;
        }
        e.stopPropagation();
      });
      input.addEventListener('input', function () {
        mtFacetSearchState.set(label, input.value);
        mtFilterFacetRows(list, input.value);
      });
    }
    var savedQuery = mtFacetSearchState.get(label) || '';
    if (input.value !== savedQuery) input.value = savedQuery;
    mtFilterFacetRows(list, savedQuery);
  }

  /* one sweep covers the desktop bar + the drawer, including visible legacy-facet groups */
  function mtAddFacetSearch(root) {
    root = root || document;
    var groups = root.querySelectorAll('details.filter-group');
    for (var i = 0; i < groups.length; i++) {
      mtWireFacetGroup(groups[i]);
    }
  }

  /* ---------- seasonal Hanukkah facet reorder, Blink (2026-09-15, subagent-built) ----------
     Same activation contract as the Boost half (handle /menorah|hanukkah/ AND Oct 1–Jan 5,
     window.__mtSeasonTest boolean override for QA; self-reverting). Three surfaces:
     desktop bar (Price to front of its .filters__container), drawer (Type to front of the
     LEGACY .blink-faceted-nav-wrapper; Price to front of the NATIVE .filters__container —
     they are two separate sibling containers and the native Price checkbox must STAY
     inside <form class="filter__form"> or formUrl()'s FormData serialization breaks its
     AJAX apply), mobile rail (Price pill right after the sort pill). Re-run after every
     applySwap (all three surfaces are innerHTML-swapped). */
  function mtSeasonActive() {
    if (typeof window.__mtSeasonTest === 'boolean') return window.__mtSeasonTest;
    var d = new Date();
    var monthDay = (d.getMonth() + 1) * 100 + d.getDate();
    return monthDay >= 1001 || monthDay <= 105;
  }

  function mtSeasonalHandleMatches() {
    return /menorah|hanukkah/i.test(location.pathname);
  }

  function mtFindDetailsByLabel(root, label, opts) {
    opts = opts || {};
    var groups = root.querySelectorAll(':scope > details.filter-group');
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      var isLegacy = /legacy-facet-/.test(g.className);
      if (opts.legacyOnly && !isLegacy) continue;
      if (opts.nonLegacyOnly && isLegacy) continue;
      if (opts.visibleOnly && g.offsetParent === null) continue;
      var h = g.querySelector('.blink-facet-heading');
      if (h && h.textContent.replace(/\s+/g, ' ').trim() === label) return g;
    }
    return null;
  }

  function mtMoveToFront(container, node) {
    if (container && node && container.firstChild !== node) {
      container.insertBefore(node, container.firstChild);
    }
  }

  function mtSeasonalReorderBlink(root) {
    root = root || document;
    if (!mtSeasonalHandleMatches() || !mtSeasonActive()) return;
    root.querySelectorAll('.mt-dgrid-blink-bar .filters__container').forEach(function (container) {
      mtMoveToFront(container, mtFindDetailsByLabel(container, 'Price', { nonLegacyOnly: true }));
    });
    root.querySelectorAll('#CollectionDrawer .blink-faceted-nav-wrapper').forEach(function (wrap) {
      mtMoveToFront(wrap, mtFindDetailsByLabel(wrap, 'Type', { legacyOnly: true, visibleOnly: true }));
    });
    root.querySelectorAll('#CollectionDrawer .filters__container').forEach(function (container) {
      mtMoveToFront(container, mtFindDetailsByLabel(container, 'Price', { nonLegacyOnly: true, visibleOnly: true }));
    });
    var pills = root.querySelector('#mt-bfr .mt-bfr-pills');
    if (pills) {
      var sortPill = pills.querySelector('.mt-bfr-sortpill');
      var pricePill = pills.querySelector('.mt-bfr-pill[data-bfr-facet="Price"]');
      if (sortPill && pricePill && sortPill.nextSibling !== pricePill) {
        pills.insertBefore(pricePill, sortPill.nextSibling);
      }
    }
  }

  function swapInner(sel, doc) {
    var live = document.querySelector(sel);
    var next = doc.querySelector(sel);
    if (live && next) live.innerHTML = next.innerHTML;
    return live && next;
  }

  function openBarDropdown(facetLabel) {
    var bar = document.querySelector('.mt-dgrid-blink-bar');
    if (!bar || !facetLabel) return;
    var groups = bar.querySelectorAll('details.filter-group');
    for (var i = 0; i < groups.length; i++) {
      var heading = groups[i].querySelector('.blink-facet-heading');
      if (heading && heading.textContent.trim() === facetLabel) { groups[i].open = true; return; }
    }
  }

  function applyAccordion(facetLabel, keepScroll) {
    /* collapse every visible group; the target facet is the only one expanded */
    var wrapper = drawer.querySelector('.collection-sidebar-wrapper');
    var prevScroll = wrapper ? wrapper.scrollTop : 0;
    var groups = drawer.querySelectorAll('details');
    var target = null;
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].offsetParent === null) continue; /* deduped/hidden groups */
      groups[i].open = false;
      var heading = groups[i].querySelector('.blink-facet-heading');
      if (!target && facetLabel && heading && heading.textContent.trim() === facetLabel) {
        target = groups[i];
      }
    }
    if (target) {
      target.open = true;
      if (keepScroll && wrapper) wrapper.scrollTop = prevScroll;
      else target.scrollIntoView({ block: 'start' });
    } else if (wrapper && !keepScroll) {
      wrapper.scrollTop = 0;
    }
  }

  function reconcileAppliedRow(doc) {
    /* the desktop applied-chips row is a conditionally-rendered SIBLING of the filter
       container (absent when no filters are active) — insert/replace/remove it to
       match the fetched render */
    var live = document.querySelector('#MainCollection .mt-dgrid-applied');
    var next = doc.querySelector('.mt-dgrid-applied');
    if (live && next) { live.innerHTML = next.innerHTML; return; }
    if (live && !next) { live.parentNode.removeChild(live); return; }
    if (!live && next) {
      var anchor = document.querySelector('.collection__filter-container');
      if (!anchor) return;
      var ref = anchor.nextElementSibling;
      if (ref && ref.classList.contains('mt-bfr-spacer')) ref = ref.nextElementSibling;
      anchor.parentNode.insertBefore(document.importNode(next, true), ref);
    }
  }

  function applySwap(doc, opts) {
    swapInner('#ProductGridContainer', doc);
    swapInner('#mt-bfr', doc);
    swapInner('#CollectionDrawer .collection-sidebar-wrapper', doc);
    swapInner('.collection__filter-container', doc);
    reconcileAppliedRow(doc);
    if (sortSheet) swapInner('.mt-bfr-sortsheet', doc);
    var liveCounts = productCountEls();
    var nextCounts = productCountEls(doc);
    for (var i = 0; i < liveCounts.length && i < nextCounts.length; i++) {
      liveCounts[i].innerHTML = nextCounts[i].innerHTML;
    }
    /* fresh markup ships the no-JS inline handlers and misses our chrome — redo */
    takeoverCheckboxes();
    injectViewInputs();
    dedupeLegacyChips();
    announceCounts();
    mtAddFacetSearch(document);
    mtSeasonalReorderBlink(document);
    refreshFooterCount();
    reinitAjaxinate();
    fillCounts();
    if (opts.surface === 'sheet' && sheetOpen) applyAccordion(opts.facetLabel || '', true);
    if (opts.surface === 'bar') openBarDropdown(opts.facetLabel || '');
    onScrollTick(); /* bar heights may have changed (chips row) — re-square pinning */
  }

  function normalizeSearch(search) {
    var p = new URLSearchParams(search);
    p.delete('page');
    var pairs = [];
    p.forEach(function (v, k) { pairs.push(k + '=' + v); });
    return pairs.sort().join('&');
  }
  var lastSearchKey = normalizeSearch(window.location.search);

  function ajaxNavigate(href, opts) {
    opts = opts || {};
    var url = new URL(withView(href), window.location.origin);
    if (!sectionId || !window.fetch || !window.DOMParser || !window.AbortController) {
      fallbackNavigate(url, opts);
      return;
    }
    var fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', sectionId);
    if (inflight) inflight.abort();
    var ctrl = new AbortController();
    inflight = ctrl;
    document.body.classList.add('mt-bfr-ajaxing');
    fetch(fetchUrl.toString(), { signal: ctrl.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (ctrl !== inflight) return; /* superseded by a newer apply */
        inflight = null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('#ProductGridContainer')) throw new Error('unexpected section payload');
        applySwap(doc, opts);
        if (opts.push !== false) history.pushState({ mtBfr: 1 }, '', url.toString());
        lastSearchKey = normalizeSearch(url.search);
        document.body.classList.remove('mt-bfr-ajaxing');
        track('mt_fv2_filter_apply', { ajax: 1 });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        document.body.classList.remove('mt-bfr-ajaxing');
        fallbackNavigate(url, opts);
      });
  }

  function fallbackNavigate(url, opts) {
    /* full reload — restore the old sheet-reopen continuity for this path */
    if (opts && opts.surface === 'sheet' && sheetOpen) markReopen(opts.facetLabel || '');
    window.location.assign(url.toString());
  }

  /* back/forward: re-render the section for the restored URL. The theme's own
     infinite-scroll pushes bare ?page=N states — identical filter state, skip those. */
  window.addEventListener('popstate', function () {
    var key = normalizeSearch(window.location.search);
    if (key === lastSearchKey) return;
    ajaxNavigate(window.location.href, { push: false });
  });

  function formUrl(form) {
    var params = new URLSearchParams(new FormData(form));
    /* empty price bounds are noise; page resets to 1 by construction */
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach(function (k) {
      if (params.has(k) && params.get(k) === '') params.delete(k);
    });
    /* the form carries no sort state — preserve the shopper's current sort */
    var cur = new URLSearchParams(window.location.search);
    if (!params.has('sort_by') && cur.get('sort_by')) params.set('sort_by', cur.get('sort_by'));
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  /* ==================== sheet / sort chrome ==================== */

  function refreshFooterCount() {
    var show = drawer.querySelector('.mt-bfr-footer-show');
    if (!show) return;
    var count = (productCountEls()[0] || {}).textContent || '';
    /* formats: "128 products" or "8 of 128 products" — the FIRST number is the
       current (filtered) result count in both */
    var n = (count.match(/\d+/) || [''])[0];
    show.textContent = 'Show results' + (n ? ' (' + n + ')' : '');
  }

  function ensureChrome() {
    if (!drawer.querySelector('.mt-bfr-sheet-header')) {
      var header = document.createElement('div');
      header.className = 'mt-bfr-sheet-header';
      header.textContent = 'Refine By';
      drawer.insertBefore(header, drawer.firstChild);
    }
    if (!drawer.querySelector('.mt-bfr-footer')) {
      var footer = document.createElement('div');
      footer.className = 'mt-bfr-footer';
      footer.innerHTML =
        '<a class="mt-bfr-footer-clear" href="' + withView(window.location.pathname.split('?')[0]) + '">Clear all</a>' +
        '<button type="button" class="mt-bfr-footer-show">Show results</button>';
      drawer.appendChild(footer);
      /* Show results is the ONE apply action: pending price edits apply here (AJAX),
         otherwise it just closes. The in-group Apply/Reset pair is hidden in the
         sheet (CSS) — four stacked buttons confused shoppers (Amy). */
      footer.querySelector('.mt-bfr-footer-show').addEventListener('click', function () {
        var gte = drawer.querySelector('input[name="filter.v.price.gte"]');
        var lte = drawer.querySelector('input[name="filter.v.price.lte"]');
        var pending = [gte, lte].some(function (el) {
          return el && (el.value || '') !== (el.getAttribute('value') || '');
        });
        if (pending && gte) {
          track('mt_fv2_filter_apply', {});
          ajaxNavigate(formUrl(gte.form), { surface: 'sheet', facetLabel: 'Price' });
        }
        closeSheet();
      });
    }
    refreshFooterCount();
  }

  /* a11y (audit 2026-09-15): overlays must expose dialog semantics, move focus in on
     open, and hand it back to their trigger on close (WCAG 2.4.3 / 4.1.2). */
  var sheetOpen = false;
  var sheetTrigger = null;
  var sortTrigger = null;
  function setDialog(el, label) {
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', label);
  }
  function unsetDialog(el) {
    el.removeAttribute('role');
    el.removeAttribute('aria-modal');
    el.removeAttribute('aria-label');
  }
  function restoreFocus(trigger) {
    if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'false');
    if (trigger && document.contains(trigger)) { try { trigger.focus(); } catch (e) { } }
  }

  function openSheet(facetLabel, trigger) {
    ensureChrome();
    sheetTrigger = trigger || null;
    if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'true');
    drawer.classList.add('mt-bfr-open');
    setDialog(drawer, 'Filters');
    if (backdrop) backdrop.hidden = false;
    document.body.classList.add('mt-bfr-lock');
    sheetOpen = true;
    track('mt_fv2_sheet_open', {});
    var closeBtn = document.getElementById('closeDrawerBtn');
    if (closeBtn) { try { closeBtn.focus(); } catch (e) { } }
    setTimeout(function () { applyAccordion(facetLabel, false); }, 120);
  }

  function closeSheet() {
    drawer.classList.remove('mt-bfr-open');
    unsetDialog(drawer);
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove('mt-bfr-lock');
    sheetOpen = false;
    restoreFocus(sheetTrigger);
    sheetTrigger = null;
  }

  function openSort(trigger) {
    if (!sortSheet) return;
    sortTrigger = trigger || null;
    if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'true');
    sortSheet.hidden = false;
    setDialog(sortSheet, 'Sort by');
    if (backdrop) backdrop.hidden = false;
    document.body.classList.add('mt-bfr-lock');
    var closeBtn = sortSheet.querySelector('.mt-bfr-sheet-close');
    if (closeBtn) { try { closeBtn.focus(); } catch (e) { } }
  }

  function closeSort() {
    if (sortSheet) { sortSheet.hidden = true; unsetDialog(sortSheet); }
    if (!sheetOpen && backdrop) backdrop.hidden = true;
    if (!sheetOpen) document.body.classList.remove('mt-bfr-lock');
    restoreFocus(sortTrigger);
    sortTrigger = null;
  }

  /* ==================== delegated interactions ==================== */

  rail.addEventListener('click', function (e) {
    var chip = e.target.closest('a.mt-bfr-chip');
    if (chip) {
      e.preventDefault();
      track('mt_fv2_chip_remove', {});
      ajaxNavigate(chip.getAttribute('href'), {});
      return;
    }
    var all = e.target.closest('[data-bfr-open]');
    if (all) { openSheet(null, all); return; }
    var sort = e.target.closest('[data-bfr-sort]');
    if (sort) { openSort(sort); track('mt_fv2_sort', {}); return; }
    var pill = e.target.closest('[data-bfr-facet]');
    if (pill) {
      track('mt_fv2_pill_tap', { facet: pill.getAttribute('data-bfr-facet') });
      openSheet(pill.getAttribute('data-bfr-facet'), pill);
    }
  });

  if (sortSheet) sortSheet.addEventListener('click', function (e) {
    if (e.target.closest('[data-bfr-sort-close]')) { closeSort(); return; }
    var opt = e.target.closest('.mt-bfr-sort-option');
    if (!opt) return;
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', opt.getAttribute('data-sort'));
    closeSort();
    ajaxNavigate(url.toString(), {});
  });

  if (backdrop) backdrop.addEventListener('click', function () {
    if (sortSheet && !sortSheet.hidden) { closeSort(); return; }
    closeSheet();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (sortSheet && !sortSheet.hidden) { closeSort(); return; }
    if (sheetOpen) closeSheet();
  });
  var nativeClose = document.getElementById('closeDrawerBtn');
  if (nativeClose) nativeClose.addEventListener('click', closeSheet);

  /* checkbox applies, both surfaces (sheet + desktop dropdown bar). The inline
     onchange submit is stripped by takeoverCheckboxes(); price inputs apply via the
     footer's Show results instead. */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.matches || !input.matches('form.filter__form input[type="checkbox"]')) return;
    if ((input.name || '').indexOf('filter.v.price') === 0) return;
    var det = input.closest('details');
    var heading = det ? det.querySelector('.blink-facet-heading') : null;
    var opts = {
      surface: input.closest('#CollectionDrawer') ? 'sheet' : 'bar',
      facetLabel: heading ? heading.textContent.trim() : ''
    };
    track('mt_fv2_filter_apply', {});
    ajaxNavigate(formUrl(input.form), opts);
  });

  /* desktop sort select (theme <collection-sort> would hard-navigate) — capture phase
     so stopPropagation() runs before the component's own listener */
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel.matches || !sel.matches('select')) return;
    if (!sel.closest('collection-sort') || !sel.closest('#MainCollection')) return;
    e.stopPropagation();
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    track('mt_fv2_sort', { value: sel.value });
    ajaxNavigate(url.toString(), {});
  }, true);

  /* remove-chips / per-facet Clear inside the drawer + injected footer Clear all */
  drawer.addEventListener('click', function (e) {
    var link = e.target.closest('a.active-facets__button, a.mt-dgrid-facet-clear, a.mt-bfr-footer-clear');
    if (!link) return;
    e.preventDefault();
    track('mt_fv2_chip_remove', {});
    ajaxNavigate(link.getAttribute('href'), { surface: 'sheet' });
  });

  /* ==================== desktop topbar (Blink) ==================== */
  var dgRoot = document.querySelector('.mt-dgrid-blink-topbar');
  if (dgRoot) {
    /* one dropdown open at a time — 'toggle' doesn't bubble, capture phase on the
       persistent section root so bar-content swaps can't orphan it */
    mainEl.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d.matches || !d.matches('.mt-dgrid-blink-bar details.filter-group') || !d.open) return;
      var bar = d.closest('.mt-dgrid-blink-bar');
      bar.querySelectorAll('details.filter-group[open]').forEach(function (o) {
        if (o !== d) o.open = false;
      });
    }, true);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.mt-dgrid-blink-bar')) return;
      document.querySelectorAll('.mt-dgrid-blink-bar details.filter-group[open]')
        .forEach(function (o) { o.open = false; });
    });
    /* All Filters button opens the drawer as a desktop slide-over (delegated —
       the button lives inside the swapped bar) */
    mainEl.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('#mtDgOpenDrawerBtn')) {
        drawer.classList.add('drawer-open');
        track('mt_fv2_sheet_open', {});
        return;
      }
      var chip = e.target.closest && e.target.closest('a.mt-dgrid-chip, a.mt-dgrid-facet-clear, a.mt-dgrid-clearall-pill');
      if (chip && !chip.closest('#CollectionDrawer')) {
        e.preventDefault();
        track('mt_fv2_chip_remove', {});
        ajaxNavigate(chip.getAttribute('href'), {});
      }
    });
    /* page backdrop for the desktop slide-over: the theme's own modal-background is a
       CHILD of the drawer (showing it veils the panel — the "black panel" bug), so we
       manage a sibling backdrop and sync it to the drawer's class for every close path */
    var dgBackdrop = document.createElement('div');
    dgBackdrop.className = 'mt-dgrid-allfilters-backdrop';
    dgBackdrop.hidden = true;
    document.body.appendChild(dgBackdrop);
    dgBackdrop.addEventListener('click', function () { drawer.classList.remove('drawer-open'); });
    /* every open/close path funnels through the class — sync the backdrop AND the
       a11y state (dialog semantics, trigger aria-expanded, focus in/out) here so no
       path can miss them (audit 2026-09-15: aria-expanded never flipped, focus never
       moved in) */
    var dgWasOpen = false;
    var dgTrigger = null;
    new MutationObserver(function () {
      var open = drawer.classList.contains('drawer-open');
      dgBackdrop.hidden = !open;
      if (open === dgWasOpen) return;
      dgWasOpen = open;
      var trigger = document.getElementById('mtDgOpenDrawerBtn');
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        dgTrigger = trigger;
        setDialog(drawer, 'All filters');
        var closeBtn = document.getElementById('closeDrawerBtn');
        if (closeBtn) { try { closeBtn.focus(); } catch (e) { } }
      } else {
        unsetDialog(drawer);
        if (dgTrigger && document.contains(dgTrigger)) { try { dgTrigger.focus(); } catch (e) { } }
        dgTrigger = null;
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ==================== init passes ==================== */
  takeoverCheckboxes();
  injectViewInputs();
  dedupeLegacyChips();
  announceCounts();
  mtAddFacetSearch(document);
  mtSeasonalReorderBlink(document);

  function markReopen(facetLabel) {
    try {
      sessionStorage.setItem('mtBfrReopen', '1');
      sessionStorage.setItem('mtBfrReopenFacet', facetLabel || '');
    } catch (e) { }
  }
  try {
    if (sessionStorage.getItem('mtBfrReopen') === '1') {
      sessionStorage.removeItem('mtBfrReopen');
      var reopenFacet = sessionStorage.getItem('mtBfrReopenFacet') || '';
      sessionStorage.removeItem('mtBfrReopenFacet');
      openSheet(reopenFacet || null);
    }
  } catch (e) { }

  track('mt_fv2_pageview', { page_type: 'collection_native' });

  /* ==================== pinned bars on scroll ==================== */
  /* Quince-style pinning. position:sticky is dead here twice over: the desktop bar
     sits under the theme's overflow:hidden main wrapper, and the mobile rail's parent
     (.blink-mobile-container) is exactly the rail's own height, so its sticky range
     is zero. Toggle fixed positioning + a spacer instead — fully LIVE per frame (no
     cached threshold or top offset; a fast scroll during load used to freeze a bad
     value). The mobile rail also measures its natural side insets at pin time and
     carries them into the fixed state as padding, so the pills don't shift
     horizontally when the bar pins (the desktop bar's stuck padding lives in CSS). */
  var pinnedBars = [];
  function addPin(bar, tuck, measurePad) {
    var spacer = document.createElement('div');
    spacer.className = 'mt-bfr-spacer';
    spacer.style.display = 'none';
    bar.parentNode.insertBefore(spacer, bar.nextSibling);
    pinnedBars.push({ bar: bar, spacer: spacer, tuck: tuck, measurePad: measurePad });
  }
  var desktopBar = document.querySelector('.mt-dgrid-blink-topbar .collection__filter-container');
  if (desktopBar && window.matchMedia('(min-width: 768px)').matches) addPin(desktopBar, 4, false);
  if (!window.matchMedia('(min-width: 768px)').matches) addPin(rail, 1, true);

  function headerH() {
    var t = 0;
    ['#shopify-section-header-top', '#shopify-section-header'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return;
      var cs = getComputedStyle(el);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        var b = el.getBoundingClientRect().bottom;
        if (b > t) t = b;
      }
    });
    return t;
  }

  var ticking = false;
  function onScrollTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var h = headerH();
      pinnedBars.forEach(function (p) {
        var bar = p.bar;
        if (!bar.classList.contains('mt-stuck')) {
          var r = bar.getBoundingClientRect();
          if (r.top <= h - 2) {
            p.spacer.style.height = bar.offsetHeight + 'px';
            p.spacer.style.display = 'block';
            if (p.measurePad) {
              bar.style.paddingLeft = Math.max(0, r.left) + 'px';
              bar.style.paddingRight = Math.max(0, document.documentElement.clientWidth - r.right) + 'px';
            }
            bar.style.top = (h - p.tuck) + 'px';
            bar.classList.add('mt-stuck');
          }
        } else {
          bar.style.top = (h - p.tuck) + 'px';
          p.spacer.style.height = bar.offsetHeight + 'px'; /* rail height can change after a swap */
          if (p.spacer.getBoundingClientRect().top > h - 2) {
            bar.classList.remove('mt-stuck');
            bar.style.top = '';
            if (p.measurePad) { bar.style.paddingLeft = ''; bar.style.paddingRight = ''; }
            p.spacer.style.display = 'none';
          }
        }
      });
    });
  }
  window.addEventListener('scroll', onScrollTick, { passive: true });
  window.addEventListener('resize', onScrollTick);
  window.addEventListener('load', onScrollTick);
  setTimeout(onScrollTick, 400); /* browser-restored scroll positions pin immediately */
})();
